import { useState } from 'react';
import produce from 'immer';
import { ColumnFilterItem } from 'antd/lib/table';
import useAsyncEffect from 'src/hooks/useAsyncEffect';
import { BugsFilters } from '../../../../types/Bugs';
import { getJiraFilterList } from 'src/components/ErrorMonitoring/api/bugs.api';

type AppSiteParams = {
  appName: string;
  siteCode: string;
};

export type Filters = {
  statuses: ColumnFilterItem[] | null;
  priorities: ColumnFilterItem[] | null;
  organizations: ColumnFilterItem[] | null;
};

const initialFilters: Filters = {
  statuses: null,
  priorities: null,
  organizations: null,
};

const initialCurrentFilters: BugsFilters = {
  bugStatus: null,
  bugPriority: null,
  organization: null,
};

const useBugsFiltersFetch = (params: AppSiteParams) => {
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [currentFilters, setCurrentFilters] = useState<BugsFilters>({ ...initialCurrentFilters });
  const [initialized, setInitialized] = useState(false);

  const changeFilters = <T extends keyof Filters>(filterKey: T, value: Filters[T]) => {
    setFilters(
      produce((draft: Filters) => {
        draft[filterKey] = value;
      })
    );
  };

  const changeCurrentFilters = (filters: Partial<BugsFilters>) => {
    setCurrentFilters((prev) => ({ ...prev, ...filters }));
  };

  const sortByName = (a: string, b: string) => {
    if (a === null || a === undefined) return -1;
    if (b === null || b === undefined) return 1;
    return a.localeCompare(b);
  };

  useAsyncEffect(async () => {
    try {
      const data = await getJiraFilterList(params.appName, params.siteCode);

      const { statusList, organizationList, priorityList } = data;

      const defaultStatuses = statusList.filter((status) => status !== 'Closed' && status !== 'Resolved').join(',');

      changeFilters(
        'statuses',
        statusList.sort(sortByName).map((name: string) => ({
          text: name,
          value: name,
        }))
      );
      changeFilters(
        'priorities',
        priorityList.sort(sortByName).map((name: string) => ({ text: name, value: name }))
      );
      changeFilters(
        'organizations',
        organizationList.sort(sortByName).map((name: string) => ({ text: name, value: name }))
      );
      if (!initialized && defaultStatuses) {
        changeCurrentFilters({ bugStatus: defaultStatuses });
        setInitialized(true);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [params.appName, params.siteCode]);

  return { filters, currentFilters, changeCurrentFilters };
};

export default useBugsFiltersFetch;
